import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { categories, articles } from '@/mocks/articles';
import ArticleCard from '@/components/base/ArticleCard';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'historical-prototype');

  const categoryArticles = useMemo(
    () => articles.filter((a) => a.categoryId === activeCategory),
    [activeCategory]
  );

  const currentCategory = categories.find((c) => c.slug === activeCategory);

  const categorySchema = {
    '@context': 'https://culturepan.github.io',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || ''} - 潘金莲文化研究中心`,
    description: currentCategory?.description || '',
    url: typeof window !== 'undefined' ? window.location.href : '',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${typeof window !== 'undefined' ? window.location.origin : ''}/article/${article.slug}`,
        name: article.title,
      })),
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://culturepan.github.io',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: typeof window !== 'undefined' ? window.location.origin : '',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: currentCategory?.name || '',
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categorySchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <main className="min-h-screen bg-background-50">
        <Navbar />

        {/* Category Header */}
        <section className="pt-24 pb-8 md:pb-12 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory?.name || '分类' },
              ]}
            />
            <div className="mt-4">
              <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-3">
                {currentCategory?.name}
              </h1>
              <p className="text-foreground-600 max-w-2xl">
                {currentCategory?.description}
              </p>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="sticky top-16 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-2 scrollbar-hide">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => {
                    setActiveCategory(cat.slug);
                    window.history.replaceState(null, '', `/category/${cat.slug}`);
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {categoryArticles.length > 0 ? (
              <>
                <div className="flex items-center justify-between mb-8">
                  <p className="text-sm text-foreground-500">
                    共 <strong className="text-foreground-900">{categoryArticles.length}</strong> 篇文章
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categoryArticles.map((article) => (
                    <ArticleCard key={article.id} {...article} />
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
                  <i className="ri-file-search-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}